import { useEffect, useMemo } from 'react';
import { Autocomplete, Box, TextField, useMediaQuery } from '@mui/material';
import { WeekFilter } from './types';

type WeekOption = {
  date: string;
  weekNumber: string;
  start: Date;
  end: Date;
};

type WeekSelectorProps = {
  onSelectWeek?: (start: Date, end: Date) => void;
  selectedIndex: number;
  setSelectedIndex: (index: number) => void;
  weekFilter: WeekFilter[];
};

const WeekSelector = ({
  onSelectWeek,
  selectedIndex,
  setSelectedIndex,
  weekFilter,
}: WeekSelectorProps) => {
  const isMobile = useMediaQuery('(max-width:600px)');

  const weekOptions = useMemo(() => {
    return weekFilter
      ?.filter((week) => week.week_number !== 1)
      .map((week) => ({
        date: `${week.week_start} - ${week.week_end}`,
        weekNumber: `Week ${week.week_number} • `,
        start: new Date(week.week_start),
        end: new Date(week.week_end),
      }));
  }, [weekFilter]);

  const handleSelectChange = (_: any, value: WeekOption | null) => {
    if (value) {
      const index = weekOptions.findIndex((week) => week.date === value.date);
      setSelectedIndex(index);
      if (onSelectWeek) {
        onSelectWeek(value.start, value.end);
      }
    }
  };

  useEffect(() => {
    if (onSelectWeek) {
      onSelectWeek(weekOptions[selectedIndex]?.start, weekOptions[selectedIndex]?.end);
    }
  }, [selectedIndex]);

  return (
    <>
      <Box sx={{ p: 0 }}>
        <Autocomplete
          id="week-autocomplete"
          options={weekOptions}
          getOptionLabel={(option) => option.weekNumber + option.date}
          value={weekOptions[selectedIndex]}
          onChange={handleSelectChange}
          disableClearable
          renderOption={(props, option) => (
            <li
              {...props}
              style={{
                color: '#6C6C6C',
                display: 'flex',
                alignItems: 'center',
                whiteSpace: 'normal',
                overflow: 'hidden',
                textOverflow: 'clip',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ fontWeight: 700, fontSize: '0.875rem' }}>{option.weekNumber}</div>
              <div style={{ fontWeight: 400, fontSize: '0.875rem' }}>{option.date}</div>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Week"
              fullWidth
              sx={{
                width: isMobile ? '8rem' : '19rem',
                height: '2.5rem',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                color: '#6C6C6C',
                fontWeight: 700,
                fontSize: '0.875rem',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#6C6C6C',
                },
                '& .MuiInputBase-root': {
                  height: '2.5rem',
                  display: 'flex',
                  alignItems: 'center',
                  paddingRight: '2rem',
                },
                '& input': {
                  padding: 0,
                  color: '#6C6C6C',
                  fontWeight: 700,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  fontSize: '0.9rem',
                },
              }}
            />
          )}
        />
      </Box>
    </>
  );
};

export default WeekSelector;
